/* eslint-disable react-memo/require-memo */
import { useCallback, useEffect, useState } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { firebaseAuth } from '../config/firebaseClient';
import { useSignMessage, useNetwork, useDisconnect } from 'wagmi';
import { getMessageToSign, getMoralisToken } from '../util/moralis/auth';
import { useWeb3Modal } from '@web3modal/react';
import { usePageLoadingContext } from '../contexts/PageLoadingContext';
import { useAlertDialog } from 'src/hooks/useAlertDialog';
import { useDialog } from 'src/hooks/useDialog';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { useGlobalComponentsContext } from 'src/contexts/GlobalComponentsContext';
import { useAuthModal } from './useAuthModal';
import { useAccountLowercase } from './useAccountLowercase';
import { useRedirectCallback } from './useRedirectCallback';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useAuth } from 'src/contexts/AuthContext';
import { LoadingButton } from 'src/components/buttons/LoadingButton';

const WALLET_AUTH_INVISIBLE = 'WALLET_AUTH_INVISIBLE' as const;
const SIGNATURE_PENDING_DIALOG = 'SIGNATURE_PENDING_DIALOG' as const;
const SIGNATURE_REJECTED_DIALOG = 'SIGNATURE_REJECTED_DIALOG' as const;
const ADDRESS_ALREADY_TAKEN_DIALOG = 'ADDRESS_ALREADY_TAKEN_DIALOG' as const;

export const useWalletAuth = () => {
  const { isConnected, address } = useAccountLowercase();
  const { closeAuthModal } = useAuthModal();
  const { union, remove } = useGlobalComponentsContext();
  const { open: openWeb3Modal, close: closeWeb3Modal } = useWeb3Modal();
  const { disconnectAsync } = useDisconnect();
  useRedirectCallback(closeWeb3Modal);
  const finishSignIn = useCallback(
    async ({ onFinish }: { onFinish?: () => void } = {}) => {
      if (!isConnected) {
        await openWeb3Modal();
      }
      closeAuthModal();
      union(
        WALLET_AUTH_INVISIBLE,
        <WalletAuthWatcher
          onFinish={() => {
            remove(WALLET_AUTH_INVISIBLE);
            remove(SIGNATURE_PENDING_DIALOG);
            if (!!onFinish) {
              onFinish();
            }
          }}
        />,
      );
    },
    [isConnected, closeAuthModal, union, openWeb3Modal, remove],
  );

  const signInWallet = useCallback(
    async ({ onFinish }: { onFinish?: () => void } = {}) => {
      if (!isConnected) {
        await openWeb3Modal();
      }
      finishSignIn({ onFinish });
    },

    [finishSignIn, isConnected, openWeb3Modal],
  );

  const resetWallet = useCallback(
    async ({ onFinish }: { onFinish?: () => void } = {}) => {
      if (isConnected) {
        await disconnectAsync();
      }
      await openWeb3Modal();
      finishSignIn({ onFinish });
    },
    [disconnectAsync, finishSignIn, isConnected, openWeb3Modal],
  );

  return {
    signInWallet,
    address,
    isConnected,
    resetWallet,
  };
};

export type WalletAuthWatcherProps = {
  onFinish: () => void;
};

function WalletAuthWatcher({ onFinish }: WalletAuthWatcherProps) {
  const { signMessageAsync } = useSignMessage({
    onError: (error, variables, context) => {
      console.error(error, variables, context);
    },
  });

  const { chain } = useNetwork();
  const { isConnected, address } = useAccountLowercase();
  const { isOpen } = useWeb3Modal();
  const { handleSignOut } = useAuth();
  useEffect(() => {
    if (!isOpen && !isConnected) {
      onFinish();
    }
  }, [isConnected, isOpen, onFinish]);

  const [isSigningInWallet, setIsSigningInWallet] = useState(true);

  const { toggleLoadingOverlay } = usePageLoadingContext();
  const { open: openRejectedDialog } = useAlertDialog(
    SIGNATURE_REJECTED_DIALOG,
  );
  const { open: openPendingDialog, close: closePendingDialog } = useDialog(
    SIGNATURE_PENDING_DIALOG,
  );
  const { open: openAddressTakenDialog, close: closeAddressTakenDialog } =
    useDialog(ADDRESS_ALREADY_TAKEN_DIALOG);

  const signMessageAndSignIn = useCallback(async () => {
    try {
      if (!address || !chain) {
        throw new Error(
          `AuthContext#signMessage failed. address is ${address} and chain is ${chain} but both must be defined.`,
        );
      }

      const { message } = await getMessageToSign(address, chain.id);

      openPendingDialog({
        title: 'Signature Request Sent.',
        description:
          'Please open your wallet and approve the signature request to sign in.',
        children: (
          <Alert severity="warning" variant="outlined" sx={{ mt: 4 }}>
            <Typography
              color="warning.light"
              sx={{ my: -1, textTransform: 'none', fontWeight: '500' }}
            >
              There is an ongoing issue with Trezor + MetaMask that makes
              signature request popups in MetaMask immediately disappear. The
              MetaMask & Trezor developer teams are aware of the bug and are
              likely to resolve it soon. If you are affected, until then, we
              recommend you use a different solution.
            </Typography>
          </Alert>
        ),
      });
      const signature = await signMessageAsync({ message });
      closePendingDialog();

      toggleLoadingOverlay(true);
      const token = await getMoralisToken({
        message,
        signature,
      });
      await signInWithCustomToken(firebaseAuth, token);
      onFinish();
    } catch (error) {
      console.warn(error);
      closePendingDialog();
      if ((error as { code: number })?.code === 4001) {
        openRejectedDialog({
          title: 'Signature Request Rejected.',
          description:
            'You have rejected the signature request sent to your wallet. You must accept the signature request in order to proceed.',
          onClose: () => {
            onFinish();
          },
        });
      } else if (
        (error as { message: string })?.message ===
        `The address: ${address} is already being used in another account.`
      ) {
        openAddressTakenDialog({
          title: 'Wallet Address Taken',
          description:
            'This wallet address is currently being used in another account. In order to sign in via the wallet address to the BluMint account associated with it, please sign out first.',
          onClose: () => {
            onFinish();
          },
          showClose: false,
          children: (
            <Stack direction={'row'} spacing={2} sx={{ width: '100%' }}>
              <LoadingButton
                onClick={async () => {
                  await handleSignOut();
                  closeAddressTakenDialog();
                }}
                variant="contained"
                color="error"
                sx={{ width: '100%' }}
              >
                Sign Out
              </LoadingButton>
              <Button
                onClick={() => {
                  closeAddressTakenDialog();
                  onFinish();
                }}
                variant="contained"
                color="primary"
                sx={{ width: '100%' }}
              >
                Nevermind
              </Button>
            </Stack>
          ),
        });
      } else {
        openRejectedDialog({
          title: 'Signature Request Rejected / Error.',
          description:
            'Either an error occured or you have rejected the signature request sent to your wallet. You must accept the signature request in order to proceed.',
          onClose: () => {
            onFinish();
          },
        });
      }
    } finally {
      toggleLoadingOverlay(false);
    }
  }, [
    address,
    chain,
    openPendingDialog,
    signMessageAsync,
    closePendingDialog,
    toggleLoadingOverlay,
    onFinish,
    openRejectedDialog,
    openAddressTakenDialog,
    handleSignOut,
    closeAddressTakenDialog,
  ]);

  useEffect(() => {
    // TODO: Handle timeout
    if (isConnected && address && chain && isSigningInWallet) {
      setIsSigningInWallet(false);
      signMessageAndSignIn();
    }
  }, [address, chain, isConnected, isSigningInWallet, signMessageAndSignIn]);
  return <></>;
}
